/**
 * @fileoverview Profile Utility Functions
 * @description Utility functions for user profile operations
 * @module utils/profile-utils
 */

export interface IdentityClaims {
  sub?: string;
  uid?: string;
  pwcguid?: string;
  pwcGUID?: string;
  email?: string;
  pwcpreferredMail?: string;
  name?: string;
  preferred_username?: string;
  [key: string]: unknown;
}

/** Maximum number of characters for profile initials */
const INITIALS_LENGTH = 2;

/**
 * Extract profile initials from identity claims
 * @description Returns the first 2 characters of the user identifier in uppercase.
 * Checks multiple claim fields in priority order: uid, pwcguid, pwcGUID,
 * preferred_username, name, email.
 * @param {IdentityClaims | null} claims - User identity claims
 * @returns {string} User initials (2 characters, uppercase) or empty string
 */
export const getProfileInitials = (claims: IdentityClaims | null): string => {
  if (!claims) {
    return '';
  }

  const rawUserId =
    claims.uid ||
    claims.pwcguid ||
    claims.pwcGUID ||
    claims.preferred_username ||
    claims.name ||
    claims.email ||
    '';

  const userId = String(rawUserId);
  return userId.substring(0, INITIALS_LENGTH).toUpperCase();
};

/**
 * Display label for the header user context pill (e.g. "contoso-platform · main").
 */
export const getUserContextLabel = (
  claims: IdentityClaims | null,
): string => {
  if (!claims) {
    return 'workspace-user · main';
  }

  const raw =
    (typeof claims.preferred_username === 'string' &&
      claims.preferred_username) ||
    (typeof claims.name === 'string' && claims.name) ||
    (typeof claims.email === 'string' && claims.email.split('@')[0]) ||
    (typeof claims.pwcpreferredMail === 'string' &&
      claims.pwcpreferredMail.split('@')[0]) ||
    'workspace-user';

  const slug = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);

  return `${slug || 'workspace-user'} · main`;
};
